
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, User, Calendar, Share } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { Skeleton } from '@/components/ui/skeleton';
import Footer from '@/components/layout/Footer';
import Navbar from '@/components/layout/Navbar';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string | null;
  content: string | null;
  author: string;
  read_time: string | null;
  image: string | null;
  group_id: string | null;
  blog_groups?: { name: string };
  created_at: string;
  updated_at: string;
  date?: string;
  tags?: string[];
}

interface CodeProps {
  node?: any;
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
}

const BlogDetail = () => {
  const { id, slug } = useParams();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setIsLoading(true);
        setError(null);
        // 从本地后端接口获取文章详情
        const response = await axios.get(`/api/blog-posts/${id}`);
        const blogPost = response.data;
        setPost({
          ...blogPost,
          date: new Date(blogPost.created_at).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }),
          tags: blogPost.tags || []
        });
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError(error instanceof Error ? error.message : "Unknown error");
        toast({
          title: "Error",
          description: "Failed to load blog post",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (id) {
      fetchBlogPost();
    }
  }, [id, toast]);
  
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: post?.title || "",
          text: post?.excerpt || "",
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast({
          title: "Link copied",
          description: "Blog post link copied to clipboard"
        });
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-primary hover:text-primary/80 mb-6">
            <ArrowLeft size={16} className="mr-2" />
            Back to Blog
          </Link>
          
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <div className="flex items-center gap-4">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-32" />
              </div>
              <Skeleton className="h-80 w-full" />
            </div>
          ) : error ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h1 className="text-2xl font-bold mb-2">Post not found</h1>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                The blog post you're looking for doesn't exist or has been removed.
              </p>
              <Link 
                to="/blog"
                className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
              >
                Return to Blog
              </Link>
            </div>
          ) : post ? (
            <article className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 md:p-8">
              <header className="mb-8">
                <h1 className="text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>
                
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                  <div className="flex items-center">
                    <Calendar size={16} className="mr-2" />
                    <span>{post.date}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <Clock size={16} className="mr-2" />
                    <span>{post.read_time || '5 min read'}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <User size={16} className="mr-2" />
                    <span>{post.author}</span>
                  </div>
                  
                  <button 
                    onClick={handleShare}
                    className="flex items-center hover:text-primary"
                  >
                    <Share size={16} className="mr-2" />
                    <span>Share</span>
                  </button>
                </div>
                
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag: string, idx: number) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                {post.image && (
                  <div className="mt-6 -mx-6 md:-mx-8">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className="w-full object-cover rounded-lg max-h-96"
                    />
                  </div>
                )}
              </header>
              
              <div className="prose dark:prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    code({node, inline, className, children, ...props}: CodeProps) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={vscDarkPlus}
                          language={match[1]}
                          PreTag="div"
                          className="rounded-md"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {post.content || ''}
                </ReactMarkdown>
              </div>
              
              {post.blog_groups && (
                <div className="mt-8 pt-4 border-t">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Posted in{' '}
                    <Link 
                      to={`/blog?group=${post.group_id}`}
                      className="text-primary hover:underline"
                    >
                      {post.blog_groups.name}
                    </Link>
                  </p>
                </div>
              )}
            </article>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h1 className="text-2xl font-bold mb-2">Post not found</h1>
              <p className="text-gray-600 dark:text-gray-300">
                The blog post you're looking for doesn't exist or has been removed.
              </p>
            </div>
          )}
          
          {relatedPosts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold mb-6">Related Posts</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <div 
                    key={relatedPost.id} 
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden"
                  >
                    {relatedPost.image && (
                      <Link to={`/blog/${relatedPost.slug}/${relatedPost.id}`}>
                        <div className="aspect-video w-full overflow-hidden">
                          <img 
                            src={relatedPost.image} 
                            alt={relatedPost.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </Link>
                    )}
                    <div className="p-4">
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-2">
                        <span>{relatedPost.date}</span>
                        <span className="mx-2">•</span>
                        <span>{relatedPost.readTime}</span>
                      </div>
                      <Link 
                        to={`/blog/${relatedPost.slug}/${relatedPost.id}`}
                        className="font-medium hover:text-primary"
                      >
                        {relatedPost.title}
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogDetail;
